import { useId, useRef, type FormEventHandler, type ReactNode } from "react";
import { X } from "lucide-react";
import clsx from "clsx";
import { Icon } from "../Icon/Icon.js";
import { useModalA11y } from "../hooks/useModalA11y.js";
import { useCloseGuard, type DialogDirty } from "../CloseGuard/CloseGuard.js";
import styles from "./Modal.module.scss";

/**
 * `Modal` sizes — the max-widths the app's modal shells actually use, kept exact so consolidating the
 * per-feature shells onto this primitive is width-preserving: `sm` 480 (compact), `md` 520 (standard editor —
 * the release/marketing default), `lg` 560 (roomier forms — the booking shells), `xl` 780 (wide pickers),
 * `2xl` 960 (the Settings workbench), `3xl` 1200 (a two-pane editor: a section rail beside a form column —
 * the show editor, whose spec card is 1340 and snaps down so a 1280 laptop keeps its gutters).
 *
 * A size is a rung ON THE PRIMITIVE, never a per-feature override: `.modal[data-size=…]` is an attribute
 * selector, so a feature class loses to it and the width silently stays wrong.
 */
export type ModalSize = "sm" | "md" | "lg" | "xl" | "2xl" | "3xl";

/**
 * How the header reads — WHAT the title is, not how loud it should be.
 *
 * - **`lockup`** (default) — STYLE-GUIDE ruling 3: display face, UPPERCASE, `fs-xl`, tracked, with a `t2`
 *   subtitle. Correct when the title NAMES THE DIALOG ("Settings", "Off / travel day", "New release") —
 *   a category label in the chrome voice, and a supporting line that is descriptive prose.
 * - **`headline`** — display face, SENTENCE case, `fs-4xl`, tight tracking, with an INK subtitle. For a
 *   dialog whose title is the SUBJECT'S OWN NAME rather than the dialog's name (the show editor's venue),
 *   and whose supporting line is FACT about that subject rather than help text. Ruling 3's lockup turns a
 *   venue into a 16px tracked chrome label, which is what it was reported as reading like (ART-88); ruling
 *   6's Archivo-Black-sentence-case treatment is what a name wants. Opt-in per call site so ruling 3
 *   stays the default everywhere else.
 */
export type ModalTitleVariant = "lockup" | "headline";

/**
 * How the BODY region is drawn — the escape hatch from the padded, scrolling body the primitive owns.
 *
 * - **`inset`** (default) — the primitive pads the body (`space-4 / space-6 / space-6`, matching the
 *   header's gutter) and scrolls it under the pinned header/footer. Every form dialog wants this; it is
 *   what stopped ~19 stylesheets each re-declaring the same triple.
 * - **`flush`** — the caller's children ARE the card's body: no padding, no scroll, and the body is a flex
 *   column that inherits the card's radius, so a child is laid out exactly as if it were a direct child of
 *   the card. For a dialog whose body is its OWN layout system rather than a stack of fields — a full-bleed
 *   identity rail that must reach the card's rounded corners (the profile dialogs), a pinned tab strip whose
 *   hairline spans the card (both Settings dialogs), a pinned filter band over a scrolling list (the support
 *   finder), a two-pane workbench that fills the card above a pinned ribbon (the show editor). All four
 *   shipped as ordinary children and were silently inset, corner-clipped and un-pinned by the 2026-09-01 body
 *   change; `flush` is the exception, not a per-dialog padding override.
 *
 * The line: **a body made of FIELDS is `inset`; a body made of PANES is `flush`.** A caller reaching for
 * `flush` to change a padding VALUE is on the wrong side of it.
 */
export type ModalBodyLayout = "inset" | "flush";

export interface ModalProps {
  /** Accessible dialog title (labels the dialog; rendered in the header unless `hideHeader`). */
  title: string;
  /**
   * Supporting line under the title — FACT ABOUT THE SUBJECT ONLY (the show's date and city under a venue
   * name; the platform and detection date under a trending headline).
   *
   * NOT a description of the dialog. STYLE-GUIDE ruling 10, extended 2026-09-01 (PO): a dialog header is its
   * title, the way it already carries no eyebrow — a line explaining what the dialog is for is chrome
   * restating the title, and the nine that shipped read as written-by-committee filler. The nine were
   * deleted; the four that survive are all data.
   */
  subtitle?: ReactNode;
  /** Header treatment — see `ModalTitleVariant`. Default `lockup`. */
  titleVariant?: ModalTitleVariant;
  /** Body treatment — see `ModalBodyLayout`. Default `inset`. */
  bodyLayout?: ModalBodyLayout;
  onClose: () => void;
  /** Max-width preset (see `ModalSize`). Default `md`. */
  size?: ModalSize;
  /** Cap the card to the viewport so a bounded body region scrolls internally under a pinned header/footer
   *  (default true). Set false for a consumer whose body has NO internal scroll region — it then grows and the
   *  backdrop scrolls the whole card (avoids clipping content, e.g. an in-body popover). */
  fitViewport?: boolean;
  /** Suppress the built-in header (the caller draws its own heading + close); the title stays as an sr-only
   *  label so `aria-labelledby` still resolves. */
  hideHeader?: boolean;
  /** aria-label for the scrim + close button (default "Close"). */
  closeLabel?: string;
  /**
   * Pinned action row below the body — it does NOT scroll with the content, which is the whole reason it is
   * a slot rather than just more children.
   *
   * The rendered element carries `data-modal-foot` so a consumer can pin "this control is in the FOOTER"
   * without reaching for a CSS-module class: vitest runs with `css: false`, so a class name is the one thing
   * a test cannot see, and WHERE a control sits is exactly the kind of ruling that otherwise goes unpinned.
   */
  footer?: ReactNode;
  /**
   * The element wrapping body + footer. `form` when the footer's submit must post the body's fields.
   *
   * This is why the footer is a slot and not a sibling: rendered as siblings, a consumer's `<form>` could
   * wrap one or the other but not both, and every form modal in the app had solved that by nesting its own
   * scroll region one level down — which is exactly what a Modal-owned body would have collided with.
   */
  bodyAs?: "div" | "form";
  /** Submit handler for `bodyAs="form"`. */
  onSubmit?: FormEventHandler<HTMLFormElement>;
  /** Extra class on the dialog card. */
  className?: string;
  /**
   * A skin hook for the BACKDROP — its scrim colour, blur and gutter — for an app whose dialog must look like its own
   * product's (Antenna's media viewer draws legacy's 50% scrim, A-58). The backdrop's mechanics stay the primitive's:
   * it is still fixed, full-viewport, centred, scrollable and at the modal layer, and the scrim button inside it still
   * closes. Additive — absent, nothing changes.
   */
  backdropClassName?: string;
  /**
   * Unsaved edits — `true`/`false`, or `"auto"` to infer them from the user typing into a field inside the dialog.
   * With edits, every dismissal (✕, scrim, Escape, a `DialogCancel` in the footer) asks "Discard changes?" first.
   * Defaults to `"auto"` for a `bodyAs="form"` modal and to never asking otherwise.
   */
  dirty?: DialogDirty;
  children: ReactNode;
}

/**
 * The centered modal primitive (centered card, fade+scale, scrim + blur(2px)). Owns the invariant
 * chrome — backdrop scrim (click-to-dismiss), the `role="dialog"`/`aria-modal` card, the header (title /
 * subtitle / close — no eyebrow: a dialog header carries no category label, ruling 10 extended), sizing
 * and the `modalIn` entrance — and folds in `useModalA11y` (Escape,
 * body-scroll freeze, focus trap, focus restore). The body is whatever the caller passes as `children`, so
 * each feature keeps its own form/footer styling. Promoted at FE-SH-02 to replace ~6 hand-rolled shells.
 */
export function Modal({
  title,
  subtitle,
  titleVariant = "lockup",
  bodyLayout = "inset",
  onClose,
  size = "md",
  hideHeader = false,
  fitViewport = true,
  footer,
  bodyAs = "div",
  onSubmit,
  closeLabel = "Close",
  className,
  backdropClassName,
  dirty,
  children,
}: ModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  const labelId = useId();
  // Every way out is one request, so a form with edits asks before any of them discards it (PO, 2026-10-04).
  const guard = useCloseGuard({ onClose, dirty: dirty ?? (bodyAs === "form" ? "auto" : undefined), root: modalRef });
  useModalA11y(modalRef, guard.requestClose);

  // WHICH of the card's edges this body actually reaches. A `flush` body paints to the card's own edges, so
  // its children clip their corners against the card's radius through an `inherit` chain — but a body sitting
  // under a header, or above a footer ribbon, meets a STRAIGHT edge there and must relay no radius at all.
  // Relaying all four unconditionally is what drew a 24px curve where the show editor's white rail met its
  // header: the card's ground showed through the wedge (reported 2026-09-07, *"the corners on the sidebar
  // shouldn't be rounded"*), and the support finder's tinted filter band had the same curve less visibly.
  // Only the modal knows this — a child cannot see whether a sibling slot is occupied — so it is decided here
  // and read by one rule per edge.
  const cardEdges = {
    "data-card-top": hideHeader || undefined,
    "data-card-bottom": footer == null || undefined,
  };

  return (
    <div className={clsx(styles.backdrop, backdropClassName)}>
      <button
        type="button"
        className={styles.scrim}
        aria-label={closeLabel}
        tabIndex={-1}
        onClick={guard.requestClose}
      />
      <div
        ref={modalRef}
        className={clsx(styles.modal, className)}
        data-size={size}
        data-fit={fitViewport ? "true" : undefined}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelId}
        tabIndex={-1}
      >
        {hideHeader ? (
          <h2 id={labelId} className={styles.srOnly}>
            {title}
          </h2>
        ) : (
          <header className={styles.head} data-title={titleVariant}>
            <div className={styles.headText}>
              <h2 id={labelId} className={styles.title}>
                {title}
              </h2>
              {subtitle != null && subtitle !== "" && <p className={styles.subtitle}>{subtitle}</p>}
            </div>
            <button type="button" className={styles.close} aria-label={closeLabel} onClick={guard.requestClose}>
              <Icon icon={X} size={20} />
            </button>
          </header>
        )}
        {/* The shell is the flex CONTEXT for the body/footer split — a `form` here can wrap both, which a
            footer rendered as the body's sibling could not. Written as two branches rather than a dynamic
            tag so each is typed against its own element. */}
        {bodyAs === "form" ? (
          <form className={styles.shell} onSubmit={onSubmit}>
            <div
              className={styles.body}
              data-layout={bodyLayout}
              data-headless={hideHeader || undefined}
              {...cardEdges}
            >
              {guard.provide(children)}
            </div>
            {footer != null && <div className={styles.foot} data-modal-foot="">{guard.provide(footer)}</div>}
          </form>
        ) : (
          <div className={styles.shell}>
            <div
              className={styles.body}
              data-layout={bodyLayout}
              data-headless={hideHeader || undefined}
              {...cardEdges}
            >
              {guard.provide(children)}
            </div>
            {footer != null && <div className={styles.foot} data-modal-foot="">{guard.provide(footer)}</div>}
          </div>
        )}
      </div>
      {guard.confirm}
    </div>
  );
}
